import { ChevronDown, Globe, Lock, Users } from "lucide-react";
import { useState } from "react";
import { useApp } from "@/context/AppContext";
import type { Team, VisibilityType } from "@/types";

const visibilityOptions: {
  value: VisibilityType;
  label: string;
  icon: typeof Lock;
  description: string;
}[] = [
  { value: "private", label: "Private", icon: Lock, description: "Only you" },
  { value: "team", label: "Team", icon: Users, description: "Team members" },
  { value: "public", label: "Public", icon: Globe, description: "Everyone" },
];

export interface VisibilityTeamSelectorProps {
  /** Current visibility value */
  visibility: VisibilityType;
  /** Current team UUID (required when visibility is "team") */
  teamUuid?: string;
  /** Called when visibility changes */
  onVisibilityChange: (visibility: VisibilityType) => void;
  /** Called when team changes (only fired when visibility is "team") */
  onTeamChange?: (teamUuid: string) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Optional label override (default: "Visibility") */
  label?: string;
  /** Whether to show the label */
  showLabel?: boolean;
  /** Compact mode for inline usage */
  compact?: boolean;
}

export function VisibilityTeamSelector({
  visibility,
  teamUuid,
  onVisibilityChange,
  onTeamChange,
  disabled = false,
  label = "Visibility",
  showLabel = true,
  compact = false,
}: Readonly<VisibilityTeamSelectorProps>) {
  const { teams } = useApp();
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  const selectedTeam = teams.find((t) => t.uuid === teamUuid);

  const handleVisibilityChange = (newVisibility: VisibilityType) => {
    if (disabled) return;
    onVisibilityChange(newVisibility);

    // When switching to team, auto-select current team or first available
    if (newVisibility === "team" && !teamUuid && onTeamChange) {
      const defaultTeam = teams.find((t) => t.isEveryoneTeam) || teams[0];
      if (defaultTeam) {
        onTeamChange(defaultTeam.uuid);
      }
    }
  };

  const handleTeamSelect = (team: Team) => {
    onTeamChange?.(team.uuid);
    setShowTeamDropdown(false);
  };

  return (
    <div>
      {showLabel && <label className="block text-sm font-medium text-content-secondary mb-1.5">{label}</label>}

      {/* Visibility toggle buttons */}
      <div className="flex gap-2">
        {visibilityOptions.map((opt) => {
          const Icon = opt.icon;
          const isSelected = visibility === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => handleVisibilityChange(opt.value)}
              className={`flex items-center gap-2 ${
                compact ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm"
              } rounded-lg border font-medium transition-colors ${
                isSelected
                  ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                  : "border-border-secondary bg-surface-secondary text-content-tertiary hover:border-border-hover hover:text-content-secondary"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Icon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Team dropdown (shown when visibility is "team") */}
      {visibility === "team" && onTeamChange && (
        <div className="relative mt-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setShowTeamDropdown(!showTeamDropdown)}
            className={`flex items-center justify-between w-full px-3 ${
              compact ? "py-1.5 text-xs" : "py-2 text-sm"
            } bg-surface-secondary border border-border-secondary rounded-lg hover:border-border-hover transition-colors ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <span className="text-content-primary">{selectedTeam?.name || "Select a team..."}</span>
            <ChevronDown
              className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-content-tertiary transition-transform ${
                showTeamDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showTeamDropdown && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                role="presentation" // NOSONAR — decorative backdrop element
                onClick={() => setShowTeamDropdown(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowTeamDropdown(false);
                  }
                }}
              />

              {/* Dropdown menu */}
              <div className="absolute top-full left-0 right-0 mt-1 bg-surface-elevated border border-border-secondary rounded-lg shadow-xl z-20 overflow-hidden max-h-48 overflow-y-auto">
                <div className="p-1">
                  {teams.map((team) => (
                    <button
                      key={team.uuid}
                      type="button"
                      onClick={() => handleTeamSelect(team)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        selectedTeam?.uuid === team.uuid
                          ? "bg-brand-primary-subtle text-brand-primary"
                          : "text-content-secondary hover:bg-interactive-hover"
                      }`}
                    >
                      <div className="font-medium">{team.name}</div>
                      {team.memberCount !== undefined && (
                        <div className="text-xs text-content-quaternary">
                          {team.memberCount} member{team.memberCount === 1 ? "" : "s"}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Simple team-only selector (for pages like NewProject that only need team picking).
 */
export interface TeamSelectorProps {
  teamId?: number;
  onTeamChange: (teamId: number, teamUuid: string) => void;
  disabled?: boolean;
  label?: string;
  showLabel?: boolean;
}

export function TeamSelector({
  teamId,
  onTeamChange,
  disabled = false,
  label = "Team",
  showLabel = true,
}: Readonly<TeamSelectorProps>) {
  const { teams } = useApp();
  const [showDropdown, setShowDropdown] = useState(false);

  const selectedTeam = teams.find((t) => t.id === teamId);

  return (
    <div>
      {showLabel && <label className="block text-sm font-medium text-content-secondary mb-1.5">{label}</label>}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setShowDropdown(!showDropdown)}
          className={`flex items-center justify-between w-full px-3 py-2 text-sm bg-surface-secondary border border-border-secondary rounded-lg hover:border-border-hover transition-colors ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4 text-brand-primary" />
            <span className="text-content-primary">{selectedTeam?.name || "Select a team..."}</span>
          </span>
          <ChevronDown
            className={`w-4 h-4 text-content-tertiary transition-transform ${showDropdown ? "rotate-180" : ""}`}
          />
        </button>

        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-10"
              role="presentation" // NOSONAR — decorative backdrop element
              onClick={() => setShowDropdown(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowDropdown(false);
                }
              }}
            />
            <div className="absolute top-full left-0 right-0 mt-1 bg-surface-elevated border border-border-secondary rounded-lg shadow-xl z-20 overflow-hidden max-h-48 overflow-y-auto">
              <div className="p-1">
                {teams.map((team) => (
                  <button
                    key={team.uuid}
                    type="button"
                    onClick={() => {
                      onTeamChange(team.id, team.uuid);
                      setShowDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedTeam?.uuid === team.uuid
                        ? "bg-brand-primary-subtle text-brand-primary"
                        : "text-content-secondary hover:bg-interactive-hover"
                    }`}
                  >
                    <div className="font-medium">{team.name}</div>
                    {team.memberCount !== undefined && (
                      <div className="text-xs text-content-quaternary">
                        {team.memberCount} member{team.memberCount === 1 ? "" : "s"}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Read-only visibility badge for display in cards/lists.
 */
export function VisibilityBadge({
  visibility,
  className = "",
}: Readonly<{
  visibility: VisibilityType;
  className?: string;
}>) {
  const opt = visibilityOptions.find((o) => o.value === visibility) ?? visibilityOptions[0];
  const Icon = opt.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs capitalize text-content-tertiary ${className}`}>
      <Icon className="h-3 w-3" />
      {opt.label}
    </span>
  );
}
